// Live, session-only parameter overrides — level/pan/tuning/mute/solo for
// pads and tracks. Deliberately NOT persisted or written back to any file:
// per-user request, editing is fine, saving to disk is a separate,
// higher-risk feature to build later. Everything here resets on reload.

import { MASTER_COMP_DEFAULTS, MASTER_EQ_DEFAULTS } from '~/lib/audio-engine.js'

function padKey(programName: string, padIndex: number) {
  return `${programName}::${padIndex}`
}
// Keyed by sequence *identity* (blobOffset), not name — sequence names can
// legitimately collide (see FORMAT.md / playback.ts's activeSequenceKey).
// sequenceKey is usually a real sequence's blobOffset, but pad-preview
// contexts outside any sequence (ChromaticStrip/PadGrid/PadMixer) pass ''
// as a sentinel that can never collide with a real numeric blobOffset.
function trackKey(sequenceKey: number | string, trackNumber: number) {
  return `${sequenceKey}::${trackNumber}`
}

export const useOverridesStore = defineStore('overrides', () => {
  const padLevel = reactive(new Map<string, number>())
  const padPan = reactive(new Map<string, number>())
  const padTuning = reactive(new Map<string, number>()) // cents, additive offset over the file's own tuning
  // 0 = One Shot (plays to completion), 1 = Note On (stops at the note's
  // recorded gate length) — see FORMAT.md. A pad's *saved* play mode isn't
  // always what actually sounds right played back through a sequencer: a
  // long phrase/loop sample saved as "Note On" plays fine live (held as
  // long as you want) but gets chopped down to whatever gate length
  // happened to get recorded once it's driven by fixed MIDI note data.
  // This override doesn't correct anything automatically (that'd risk
  // silently breaking genuinely-gated hits elsewhere) — it just lets a
  // pad that's audibly wrong be flipped by ear, same as level/pan/tuning.
  const padPlayMode = reactive(new Map<string, number>())
  const padReverse = reactive(new Map<string, boolean>())
  // Pads sharing a sample name with a complete gapless chopIndex run are
  // chop-family candidates (see pgm.js). Stored NDC families normally use
  // the saved source-chop byte when present; 'pad' A/Bs that against the
  // pad-order chopIndex, and 'full' disables the pad-level chop trim.
  const padChopMode = reactive(new Map<string, 'pad' | 'full'>())
  // AMP envelope (PGM attack@0x66/decay@0x67/sustain@0x69/release@0x6a —
  // see FORMAT.md and audio-engine.js's decaySeconds/releaseSeconds).
  // decayMode isn't editable here yet — it's a Start/End mode switch, not
  // a continuous 0-100 knob like these four.
  const padAttack = reactive(new Map<string, number>())
  const padDecay = reactive(new Map<string, number>())
  const padSustain = reactive(new Map<string, number>())
  const padRelease = reactive(new Map<string, number>())
  const trackLevel = reactive(new Map<string, number>())
  const trackPan = reactive(new Map<string, number>())
  const trackMuted = reactive(new Map<string, boolean>())
  const trackSoloed = reactive(new Set<string>())
  // Global "tape speed" knob — a fun playback toy, not per-project state:
  // 1.0 = normal, scales tempo and pitch together (see audio-engine.js's
  // playSequence). Reset on project load along with everything else so a
  // forgotten knob turn doesn't carry over and confuse the next project.
  const varispeed = ref(1)
  // FX send bus (Trk Mixer's "Fx" button / Send row, CC#91 — see
  // audio-engine.js's constructor and FORMAT.md). Off by default: the
  // file's own stored send level defaults to 30 on ~99% of real tracks,
  // and turning that into an always-on effect tail would quietly change
  // how every existing project sounds rather than reproducing something
  // already audible. Global rather than per-track since there's only one
  // shared send bus right now, matching the single "Fx" toggle in the UI.
  const fxEnabled = ref(false)
  // Per-track live send override — same idea as trackLevel/trackPan, but
  // for how much of a track's signal reaches the shared FX bus. Until
  // touched, falls back to the file's own recorded send (or CC#91
  // automation, see hasTrackSendOverride) rather than a fixed default.
  const trackSend = reactive(new Map<string, number>())
  // Two independently-typed FX slots on the shared send bus, matching
  // JJOS's FX1/FX2 onboard rack (see FxWindow.vue's FX_TYPES for the
  // full per-slot type list and audio-engine.js's _buildFxEffect for the
  // DSP). FX1 defaults to Reverb (this app's original single effect, kept
  // as the default so existing behavior doesn't change); FX2 defaults off,
  // same as the old shared bus's overall on/off default. Session-only,
  // reset to these defaults on project load.
  const DEFAULT_REVERB_DECAY = 1.8
  const fx1Type = ref('reverb')
  const fx1Params = reactive<Record<string, number>>({ decay: DEFAULT_REVERB_DECAY })
  const fx2Type = ref('off')
  const fx2Params = reactive<Record<string, number>>({})
  // Master EQ / Master Compressor — JJOS's own master-bus insert effects
  // (the "FX" screen in the OS manual, right above FX1/FX2), applied to
  // the whole summed mix rather than a parallel send like FX1/FX2. Off
  // by default, same reasoning as fxEnabled above. Keyed by 'eq'|'comp'
  // (matching audio-engine.js's setMasterInsertEnabled/setMasterInsertParam)
  // rather than four separate refs, so a third master insert is one more
  // key instead of a whole new set of fields/functions. Spread the
  // shared defaults rather than aliasing them — this is a live, mutable
  // per-session copy, not the canonical default object.
  const masterInsertEnabled = reactive<Record<'eq' | 'comp', boolean>>({ eq: false, comp: false })
  const masterInsertParams = reactive<Record<'eq' | 'comp', Record<string, number>>>({
    eq: { ...MASTER_EQ_DEFAULTS },
    comp: { ...MASTER_COMP_DEFAULTS },
  })

  // --- Pads ---
  function getPadLevel(programName: string, pad: any) {
    if (!pad) return 100
    return padLevel.get(padKey(programName, pad.index)) ?? pad?.mixerLevel ?? 100
  }
  function setPadLevel(programName: string, padIndex: number, value: number) {
    padLevel.set(padKey(programName, padIndex), Math.max(0, Math.min(127, value)))
  }
  function getPadPan(programName: string, pad: any) {
    if (!pad) return 50
    return padPan.get(padKey(programName, pad.index)) ?? pad?.mixerPan ?? 50
  }
  function setPadPan(programName: string, padIndex: number, value: number) {
    padPan.set(padKey(programName, padIndex), Math.max(0, Math.min(100, value)))
  }
  function getPadTuningOffset(programName: string, pad: any) {
    if (!pad) return 0
    return padTuning.get(padKey(programName, pad.index)) ?? 0
  }
  function setPadTuningOffset(programName: string, padIndex: number, cents: number) {
    padTuning.set(padKey(programName, padIndex), Math.max(-1200, Math.min(1200, cents)))
  }
  function getPadAttack(programName: string, pad: any) {
    if (!pad) return 0
    return padAttack.get(padKey(programName, pad.index)) ?? pad?.attack ?? 0
  }
  function setPadAttack(programName: string, padIndex: number, value: number) {
    padAttack.set(padKey(programName, padIndex), Math.max(0, Math.min(100, value)))
  }
  function getPadDecay(programName: string, pad: any) {
    if (!pad) return 0
    return padDecay.get(padKey(programName, pad.index)) ?? pad?.decay ?? 0
  }
  function setPadDecay(programName: string, padIndex: number, value: number) {
    padDecay.set(padKey(programName, padIndex), Math.max(0, Math.min(100, value)))
  }
  function getPadSustain(programName: string, pad: any) {
    if (!pad) return 100
    return padSustain.get(padKey(programName, pad.index)) ?? pad?.sustain ?? 100
  }
  function setPadSustain(programName: string, padIndex: number, value: number) {
    padSustain.set(padKey(programName, padIndex), Math.max(0, Math.min(100, value)))
  }
  function getPadRelease(programName: string, pad: any) {
    if (!pad) return 0
    return padRelease.get(padKey(programName, pad.index)) ?? pad?.release ?? 0
  }
  function setPadRelease(programName: string, padIndex: number, value: number) {
    padRelease.set(padKey(programName, padIndex), Math.max(0, Math.min(100, value)))
  }
  function getPadPlayModeOverride(programName: string, pad: any) {
    if (!pad) return null
    const k = padKey(programName, pad.index)
    return padPlayMode.has(k) ? padPlayMode.get(k)! : null
  }
  function togglePadPlayMode(programName: string, pad: any) {
    if (!pad) return
    const k = padKey(programName, pad.index)
    const current = padPlayMode.has(k) ? padPlayMode.get(k)! : pad.samples?.[0]?.playMode ?? 0
    padPlayMode.set(k, current === 1 ? 0 : 1)
  }
  function getPadReverse(programName: string, pad: any) {
    if (!pad) return false
    const k = padKey(programName, pad.index)
    return padReverse.has(k) ? !!padReverse.get(k) : !!pad.reverse
  }
  function togglePadReverse(programName: string, pad: any) {
    if (!pad) return
    const k = padKey(programName, pad.index)
    padReverse.set(k, !getPadReverse(programName, pad))
  }
  function getChopMode(programName: string, pad: any): 'equal' | 'pad' | 'full' {
    if (!pad) return 'equal'
    return padChopMode.get(padKey(programName, pad.index)) ?? 'equal'
  }
  function cycleChopMode(programName: string, pad: any, hasStoredWindows = false) {
    if (!pad) return
    const k = padKey(programName, pad.index)
    const current = getChopMode(programName, pad)
    if (hasStoredWindows && current === 'equal') padChopMode.set(k, 'pad')
    else if (current === 'pad') padChopMode.set(k, 'full')
    else if (current === 'full') padChopMode.delete(k)
    else padChopMode.set(k, 'full')
  }
  function resetPad(programName: string, padIndex: number) {
    const k = padKey(programName, padIndex)
    padLevel.delete(k)
    padPan.delete(k)
    padTuning.delete(k)
    padPlayMode.delete(k)
    padReverse.delete(k)
    padChopMode.delete(k)
    padAttack.delete(k)
    padDecay.delete(k)
    padSustain.delete(k)
    padRelease.delete(k)
  }

  // --- Tracks ---
  function getTrackLevel(sequenceKey: number | string, track: any) {
    return trackLevel.get(trackKey(sequenceKey, track.number)) ?? track?.level ?? 100
  }
  function setTrackLevel(sequenceKey: number | string, trackNumber: number, value: number) {
    trackLevel.set(trackKey(sequenceKey, trackNumber), Math.max(0, Math.min(127, value)))
  }
  function getTrackPan(sequenceKey: number | string, track: any) {
    return trackPan.get(trackKey(sequenceKey, track.number)) ?? track?.pan ?? 50
  }
  function setTrackPan(sequenceKey: number | string, trackNumber: number, value: number) {
    trackPan.set(trackKey(sequenceKey, trackNumber), Math.max(0, Math.min(100, value)))
  }
  // Distinct from getTrackLevel/getTrackPan (which always resolve to
  // *some* value, file-derived or not) — these answer "has the user
  // actually touched this fader this session," so recorded Track Mixer
  // CC#7/10 automation (see audio-engine.js) knows whether it's still
  // safe to keep driving a track's live level/pan, or whether the user's
  // own edit already takes precedence.
  function hasTrackLevelOverride(sequenceKey: number | string, trackNumber: number) {
    return trackLevel.has(trackKey(sequenceKey, trackNumber))
  }
  function hasTrackPanOverride(sequenceKey: number | string, trackNumber: number) {
    return trackPan.has(trackKey(sequenceKey, trackNumber))
  }
  function getTrackSend(sequenceKey: number | string, track: any) {
    return trackSend.get(trackKey(sequenceKey, track.number)) ?? track?.send ?? 30
  }
  function setTrackSend(sequenceKey: number | string, trackNumber: number, value: number) {
    trackSend.set(trackKey(sequenceKey, trackNumber), Math.max(0, Math.min(100, value)))
  }
  function hasTrackSendOverride(sequenceKey: number | string, trackNumber: number) {
    return trackSend.has(trackKey(sequenceKey, trackNumber))
  }
  /** Clears the override entirely (not "set to the file's value") — lets recorded CC#91 automation apply again too, matching hasTrackSendOverride's gate. */
  function resetTrackSend(sequenceKey: number | string, trackNumber: number) {
    trackSend.delete(trackKey(sequenceKey, trackNumber))
  }
  // `track.muted` is the file's own saved mute state (offset 0x29 — see
  // FORMAT.md); a live user toggle in the Trk Mixer overrides it for the
  // rest of the session, but until touched, a track the user actually
  // muted on the hardware should stay muted here too — otherwise a project
  // with several layered audio takes (mixed down with some intentionally
  // muted) plays all of them at once, which is not what was saved.
  function isTrackMuted(sequenceKey: number | string, trackNumber: number, track?: any) {
    const k = trackKey(sequenceKey, trackNumber)
    return trackMuted.has(k) ? !!trackMuted.get(k) : !!track?.muted
  }
  function isTrackSoloed(sequenceKey: number | string, trackNumber: number) {
    return trackSoloed.has(trackKey(sequenceKey, trackNumber))
  }
  function toggleTrackMute(sequenceKey: number | string, trackNumber: number, track?: any) {
    const k = trackKey(sequenceKey, trackNumber)
    trackMuted.set(k, !isTrackMuted(sequenceKey, trackNumber, track))
  }
  function toggleTrackSolo(sequenceKey: number | string, trackNumber: number) {
    const k = trackKey(sequenceKey, trackNumber)
    if (trackSoloed.has(k)) trackSoloed.delete(k)
    else trackSoloed.add(k)
  }
  function anySoloedIn(sequenceKey: number | string) {
    const prefix = sequenceKey + '::'
    for (const k of trackSoloed) if (k.startsWith(prefix)) return true
    return false
  }
  /** Final audibility folding mute + solo (real hardware rule: if anything's soloed, only soloed tracks play). */
  function isTrackAudible(sequenceKey: number | string, trackNumber: number, track?: any) {
    const k = trackKey(sequenceKey, trackNumber)
    if (anySoloedIn(sequenceKey)) return trackSoloed.has(k)
    return !isTrackMuted(sequenceKey, trackNumber, track)
  }

  let varispeedRestartTimer: ReturnType<typeof setTimeout> | null = null
  function setVarispeed(value: number) {
    varispeed.value = Math.max(0.5, Math.min(1.5, value))
    // Retune whatever's currently sounding immediately — gives audible
    // feedback the instant the knob moves, rather than only at the next
    // scheduling pass.
    const playback = usePlaybackStore()
    playback.activeController?.setVarispeed?.(varispeed.value)
    // Once the knob stops moving (debounced — a drag fires this on every
    // pixel), do a full clean stop+restart at the new rate. Live-patching
    // alone only touches already-scheduled sources; a real restart is the
    // only way to guarantee no scheduling/choke-group state from the old
    // rate lingers. Restarting on every intermediate tick instead would
    // just jerk the sequence back to bar 1 repeatedly mid-drag, which is
    // worse than what this replaces.
    if (varispeedRestartTimer) clearTimeout(varispeedRestartTimer)
    varispeedRestartTimer = setTimeout(() => {
      varispeedRestartTimer = null
      playback.restartIfPlaying(useProjectStore().programsByName)
    }, 250)
  }

  /**
   * The "Fx" button in TrkMixer.vue — flips the shared send bus on/off for
   * the whole session and immediately pushes the change onto every bus in
   * whatever's currently playing (mirrors syncAllTrackBuses' solo-toggle
   * pattern), rather than waiting for the next note/loop to pick it up.
   */
  function toggleFxEnabled() {
    fxEnabled.value = !fxEnabled.value
    const playback = usePlaybackStore()
    const seq = playback.focusedSequence
    if (seq) playback.syncAllTrackBuses(seq.blobOffset, seq.tracks)
  }

  /** FX Settings window's type picker for a slot ('fx1'|'fx2') — swaps the effect running there, live, and resets its params to the new type's defaults. */
  function setFxType(slot: 'fx1' | 'fx2', type: string, defaultParams: Record<string, number> = {}) {
    const paramsRef = slot === 'fx1' ? fx1Params : fx2Params
    for (const k of Object.keys(paramsRef)) delete paramsRef[k]
    Object.assign(paramsRef, defaultParams)
    if (slot === 'fx1') fx1Type.value = type
    else fx2Type.value = type
    useAudioEngine().setFxType(slot, type, defaultParams)
  }

  /** FX Settings window's per-param sliders — live-tweaks one param of whatever effect is currently active in a slot, no graph rebuild. */
  function setFxParam(slot: 'fx1' | 'fx2', name: string, value: number) {
    const paramsRef = slot === 'fx1' ? fx1Params : fx2Params
    paramsRef[name] = value
    useAudioEngine().setFxParam(slot, name, value)
  }

  /** FX Settings window — Master EQ/Compressor on/off toggle. */
  function toggleMasterInsertEnabled(which: 'eq' | 'comp') {
    masterInsertEnabled[which] = !masterInsertEnabled[which]
    useAudioEngine().setMasterInsertEnabled(which, masterInsertEnabled[which])
  }

  /** FX Settings window — live-tweak one Master EQ/Compressor param, no graph rebuild. */
  function setMasterInsertParam(which: 'eq' | 'comp', name: string, value: number) {
    masterInsertParams[which][name] = value
    useAudioEngine().setMasterInsertParam(which, name, value)
  }

  /** Build the plain resolver object audio-engine.js expects, bound to one sequence (by its unique blobOffset). */
  function resolverFor(sequenceKey: number | string) {
    return {
      getPadLevel,
      getPadPan,
      getPadTuningOffset,
      getPadAttack,
      getPadDecay,
      getPadSustain,
      getPadRelease,
      getPadPlayModeOverride,
      getPadReverse,
      getChopMode,
      getTrackLevel: (trackNumber: number, track: any) => getTrackLevel(sequenceKey, track),
      getTrackPan: (trackNumber: number, track: any) => getTrackPan(sequenceKey, track),
      getTrackSend: (trackNumber: number, track: any) => getTrackSend(sequenceKey, track),
      isTrackAudible: (trackNumber: number, track: any) => isTrackAudible(sequenceKey, trackNumber, track),
      getVarispeed: () => varispeed.value,
      hasTrackLevelOverride: (trackNumber: number) => hasTrackLevelOverride(sequenceKey, trackNumber),
      hasTrackPanOverride: (trackNumber: number) => hasTrackPanOverride(sequenceKey, trackNumber),
      hasTrackSendOverride: (trackNumber: number) => hasTrackSendOverride(sequenceKey, trackNumber),
      isFxEnabled: () => fxEnabled.value,
    }
  }

  /**
   * Program/sequence names collide constantly across projects ("Program01",
   * "Sequence01" are near-universal) — call on every project load, or a
   * live edit made in one project would silently carry over into the next
   * one that happens to reuse the same names.
   */
  function resetAll() {
    padLevel.clear()
    padPan.clear()
    padTuning.clear()
    padPlayMode.clear()
    padReverse.clear()
    padChopMode.clear()
    padAttack.clear()
    padDecay.clear()
    padSustain.clear()
    padRelease.clear()
    trackLevel.clear()
    trackPan.clear()
    trackMuted.clear()
    trackSoloed.clear()
    trackSend.clear()
    varispeed.value = 1
    fxEnabled.value = false
    setFxType('fx1', 'reverb', { decay: DEFAULT_REVERB_DECAY })
    setFxType('fx2', 'off')
    masterInsertEnabled.eq = false
    masterInsertEnabled.comp = false
    Object.assign(masterInsertParams.eq, MASTER_EQ_DEFAULTS)
    Object.assign(masterInsertParams.comp, MASTER_COMP_DEFAULTS)
    useAudioEngine().resetMasterInserts()
  }

  return {
    getPadLevel,
    setPadLevel,
    getPadPan,
    setPadPan,
    getPadTuningOffset,
    setPadTuningOffset,
    getPadAttack,
    setPadAttack,
    getPadDecay,
    setPadDecay,
    getPadSustain,
    setPadSustain,
    getPadRelease,
    setPadRelease,
    getPadPlayModeOverride,
    togglePadPlayMode,
    getPadReverse,
    togglePadReverse,
    getChopMode,
    cycleChopMode,
    resetPad,
    getTrackLevel,
    setTrackLevel,
    getTrackPan,
    setTrackPan,
    getTrackSend,
    setTrackSend,
    resetTrackSend,
    hasTrackLevelOverride,
    hasTrackPanOverride,
    hasTrackSendOverride,
    isTrackMuted,
    isTrackSoloed,
    toggleTrackMute,
    toggleTrackSolo,
    isTrackAudible,
    varispeed,
    setVarispeed,
    fxEnabled,
    toggleFxEnabled,
    fx1Type,
    fx1Params,
    fx2Type,
    fx2Params,
    setFxType,
    setFxParam,
    masterInsertEnabled,
    masterInsertParams,
    toggleMasterInsertEnabled,
    setMasterInsertParam,
    resolverFor,
    resetAll,
  }
})
